// Prevent redefinition if the script is loaded multiple times
if (typeof window.CartCountdown === 'undefined') {
  window.CartCountdown = class extends HTMLElement {
    static timerInterval = null;
    static instances = new Set();
    static DURATION = 13 * 60; // 13 minutes in seconds
    static STORAGE_KEY = 'cartCountdown';
    static SESSION_EXPIRED_KEY = 'cartCountdownExpired';
    static ATTRIBUTE_KEY = 'countdown_end';
    static lastUpdateTime = 0;
    static rafId = null;
    static sharedObserver = null;
    static updateQueue = new Set();
    static isUpdating = false;
    static stringCache = new Map();
    static isInitializing = false; // Prevent concurrent initialization

    // Performance monitoring
    static perf = {
      marks: new Map(),
      measure(name, start, end) {
        if (performance.mark && performance.measure) {
          try {
            // If start and end are numbers (timestamps), use them directly
            if (typeof start === 'number' && typeof end === 'number') {
              performance.measure(name, { start, end });
            } else {
              // If they are mark names, use them as is
              performance.measure(name, start, end);
            }
          } catch (e) {
            // Ignore performance API errors
            console.warn('Performance API error:', e);
          }
        }
      }
    };

    constructor() {
      super();
      this.selector = this.getAttribute('selector') || null;
      this.display = null;
      this.lastDisplayValue = null;
      this.isVisible = true;
      this.isDestroyed = false;
      this.cachedDisplay = null;

      // Bind methods for better performance
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    connectedCallback() {
      try {
        const startTime = performance.now();

        // Cache DOM query
        this.cachedDisplay = this.selector
          ? document.querySelector(this.selector)
          : this.querySelector('.cart-countdown-display') || this;

        this.display = this.cachedDisplay;

        CartCountdown.instances.add(this);

        // Use shared observer for better memory efficiency
        this.setupSharedVisibilityObserver();

        // Prevent race condition: only ONE instance should initialize
        if (!CartCountdown.timerInterval && !CartCountdown.isInitializing) {
          this.startCountdown();
        } else {
          // If timer is already running or initializing, just update this instance's display
          const endTime = localStorage.getItem(CartCountdown.STORAGE_KEY);
          const currentTime = Math.floor(Date.now() / 1000);
          let timeLeft = endTime
            ? parseInt(endTime, 10) - currentTime
            : CartCountdown.DURATION;
          // Never render 00:00 on connect — kick a restart if already expired
          if (timeLeft <= 0) {
            this.restartCountdown().then((newEnd) => {
              const left = newEnd
                ? Math.max(1, parseInt(newEnd, 10) - Math.floor(Date.now() / 1000))
                : CartCountdown.DURATION;
              this.updateDisplay(left);
            });
          } else {
            this.updateDisplay(timeLeft);
          }
        }

        // Measure performance properly
        const endTime = performance.now();
        CartCountdown.perf.measure('countdown-init', startTime, endTime);
      } catch (error) {
        console.warn('CartCountdown initialization error:', error);
      }
    }

    disconnectedCallback() {
      this.isDestroyed = true;
      CartCountdown.instances.delete(this);
      CartCountdown.updateQueue.delete(this);

      if (CartCountdown.sharedObserver) {
        CartCountdown.sharedObserver.unobserve(this);
      }

      if (CartCountdown.instances.size === 0) {
        this.cleanup();
      }
    }

    cleanup() {
      clearInterval(CartCountdown.timerInterval);
      CartCountdown.timerInterval = null;
      CartCountdown.isInitializing = false;

      if (CartCountdown.rafId) {
        cancelAnimationFrame(CartCountdown.rafId);
        CartCountdown.rafId = null;
      }

      if (CartCountdown.sharedObserver) {
        CartCountdown.sharedObserver.disconnect();
        CartCountdown.sharedObserver = null;
      }

      // Clear caches
      CartCountdown.stringCache.clear();
      CartCountdown.updateQueue.clear();
      CartCountdown.isUpdating = false;
    }

    setupSharedVisibilityObserver() {
      if (!('IntersectionObserver' in window)) return;

      if (!CartCountdown.sharedObserver) {
        CartCountdown.sharedObserver = new IntersectionObserver((entries) => {
          // Use requestIdleCallback for non-critical updates
          const processEntries = () => {
            entries.forEach(entry => {
              if (entry.target instanceof CartCountdown) {
                entry.target.handleVisibilityChange(entry.isIntersecting);
              }
            });
          };

          if (window.requestIdleCallback) {
            requestIdleCallback(processEntries, { timeout: 100 });
          } else {
            setTimeout(processEntries, 0);
          }
        }, {
          threshold: 0.1,
          rootMargin: '50px' // Preload slightly before visible
        });
      }

      CartCountdown.sharedObserver.observe(this);
    }

    handleVisibilityChange(isVisible) {
      if (this.isDestroyed) return;

      const wasVisible = this.isVisible;
      this.isVisible = isVisible;

      // Only trigger update if visibility changed and now visible
      if (!wasVisible && isVisible) {
        CartCountdown.updateQueue.add(this);
        this.scheduleUpdate();
      } else if (!isVisible) {
        CartCountdown.updateQueue.delete(this);
      }
    }

    // Optimized string formatting with caching
    formatTime(timeLeft) {
      const safeLeft = (!Number.isFinite(timeLeft) || timeLeft <= 0)
        ? CartCountdown.DURATION
        : timeLeft;

      if (CartCountdown.stringCache.has(safeLeft)) {
        return CartCountdown.stringCache.get(safeLeft);
      }

      const minutes = Math.floor(safeLeft / 60);
      const seconds = safeLeft % 60;

      // Micro-optimization: use template literal for better performance
      const formatted = `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;

      // Cache with LRU-like behavior (limit cache size)
      if (CartCountdown.stringCache.size > 100) {
        const firstKey = CartCountdown.stringCache.keys().next().value;
        CartCountdown.stringCache.delete(firstKey);
      }

      CartCountdown.stringCache.set(safeLeft, formatted);
      return formatted;
    }

    updateDisplay(timeLeft) {
      if (this.isDestroyed || !this.isVisible || !this.display) return;

      const formatted = this.formatTime(timeLeft);

      // Debounced DOM update - only update when display value changes
      if (this.lastDisplayValue !== formatted) {
        this.lastDisplayValue = formatted;

        // Use textContent for better performance than innerHTML
        this.display.textContent = formatted;

        // Dispatch custom event for other components (optional)
        this.dispatchEvent(new CustomEvent('countdown-update', {
          detail: { timeLeft, formatted },
          bubbles: false // Don't bubble for performance
        }));
      }
    }

    scheduleUpdate() {
      if (CartCountdown.isUpdating || CartCountdown.updateQueue.size === 0) return;

      CartCountdown.isUpdating = true;

      if (CartCountdown.rafId) {
        cancelAnimationFrame(CartCountdown.rafId);
      }

      CartCountdown.rafId = requestAnimationFrame(() => {
        this.processBatchUpdate();
        CartCountdown.isUpdating = false;
      });
    }

    processBatchUpdate() {
      if (CartCountdown.updateQueue.size === 0) return;

      const endTime = localStorage.getItem(CartCountdown.STORAGE_KEY);
      const currentTime = Math.floor(Date.now() / 1000);
      let timeLeft = endTime
        ? parseInt(endTime, 10) - currentTime
        : CartCountdown.DURATION;

      // Never push 00:00 — restart storage immediately, then show fresh window
      if (!Number.isFinite(timeLeft) || timeLeft <= 0) {
        this.restartCountdown().then((newEnd) => {
          const left = newEnd
            ? Math.max(1, parseInt(newEnd, 10) - Math.floor(Date.now() / 1000))
            : CartCountdown.DURATION;
          CartCountdown.instances.forEach((instance) => {
            if (!instance.isDestroyed && instance.isVisible) {
              instance.updateDisplay(left);
            }
          });
        });
        timeLeft = CartCountdown.DURATION;
      }

      // Batch process all visible instances
      CartCountdown.updateQueue.forEach(instance => {
        if (!instance.isDestroyed && instance.isVisible) {
          instance.updateDisplay(timeLeft);
        }
      });

      CartCountdown.updateQueue.clear();
    }

    async startCountdown() {
      // Set flag immediately to prevent race condition
      CartCountdown.isInitializing = true;

      try {
        // If a previous session marked expiry, clear it — we always restart instead of sticking at 00:00
        try {
          sessionStorage.removeItem(CartCountdown.SESSION_EXPIRED_KEY);
        } catch (e) {
          // ignore
        }

        const now = Math.floor(Date.now() / 1000);
        // Try to get a shared timestamp from cart attributes first
        let endTime = await this.fetchCartAttributeEndTime().catch(() => null);
        if (!endTime) {
          endTime = localStorage.getItem(CartCountdown.STORAGE_KEY);
        }

        if (!endTime || parseInt(endTime, 10) <= now) {
          endTime = now + CartCountdown.DURATION;
          try {
            localStorage.setItem(CartCountdown.STORAGE_KEY, endTime.toString());
          } catch (e) {
            // Fallback if localStorage is not available
            console.warn('localStorage not available, countdown will reset on page refresh');
          }
          // Persist to cart attribute so Checkout UI can read it
          try {
            await this.setCartAttributeEndTime(endTime);
          } catch (e) {
            // Non-fatal
            console.warn('Failed to set cart attribute for countdown:', e);
          }
        }

        const updateTimer = async () => {
          try {
            const currentTime = Math.floor(Date.now() / 1000);
            let parsedEnd = parseInt(endTime, 10);
            let timeLeft = parsedEnd - currentTime;

            // Never show 00:00 — reset immediately to a fresh duration window
            if (timeLeft <= 0) {
              const restarted = await this.restartCountdown();
              if (restarted) {
                endTime = restarted;
                parsedEnd = parseInt(endTime, 10);
                timeLeft = Math.max(1, parsedEnd - Math.floor(Date.now() / 1000));
              } else {
                timeLeft = CartCountdown.DURATION;
              }
            }

            // Yield to prevent blocking main thread
            if (CartCountdown.instances.size > 5) { // Only yield if many instances
              await this.yieldToMain();
            }

            // Add all visible instances to update queue
            CartCountdown.instances.forEach(instance => {
              if (instance.isVisible && !instance.isDestroyed) {
                CartCountdown.updateQueue.add(instance);
              }
            });

            // Schedule batch update
            this.scheduleUpdate();
          } catch (error) {
            console.warn('Timer update error:', error);
          }
        };

        // Initial update
        await updateTimer();

        // Use setInterval with error handling
        CartCountdown.timerInterval = setInterval(updateTimer, 1000);
      } finally {
        // Always unset the flag when initialization completes
        CartCountdown.isInitializing = false;
      }
    }

    /** Restart countdown from now + DURATION. Returns new endTime (number|string) or null. */
    async restartCountdown() {
      const now = Math.floor(Date.now() / 1000);
      const newEndTime = now + CartCountdown.DURATION;

      try {
        sessionStorage.removeItem(CartCountdown.SESSION_EXPIRED_KEY);
      } catch (e) {
        // ignore
      }

      try {
        localStorage.setItem(CartCountdown.STORAGE_KEY, newEndTime.toString());
      } catch (e) {
        console.warn('localStorage not available, countdown restart may not persist');
      }

      try {
        await this.setCartAttributeEndTime(newEndTime);
      } catch (e) {
        // Non-fatal
      }

      document.dispatchEvent(new CustomEvent('cart-countdown-complete', {
        detail: { instances: CartCountdown.instances.size, restarted: true }
      }));

      return newEndTime;
    }

    handleCountdownComplete() {
      // Kept for backwards compatibility — prefer restartCountdown() so UI never sticks at 00:00
      this.restartCountdown().then((newEndTime) => {
        if (!newEndTime) return;
        const timeLeft = CartCountdown.DURATION;
        CartCountdown.instances.forEach(instance => {
          if (!instance.isDestroyed) {
            instance.updateDisplay(timeLeft);
          }
        });
      });
    }

    // Optimized yield function with multiple strategies
    async yieldToMain() {
      return new Promise(resolve => {
        if (typeof scheduler !== 'undefined' && scheduler.postTask) {
          scheduler.postTask(resolve, { priority: 'background' });
        } else if (window.requestIdleCallback) {
          requestIdleCallback(resolve, { timeout: 50 });
        } else {
          setTimeout(resolve, 0);
        }
      });
    }

    // ===== Shared timestamp via Cart Attribute =====
    async fetchCartAttributeEndTime() {
      try {
        const cartUrl = (window.routes && window.routes.cart.url) || '/cart';
        const res = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
        if (!res.ok) return null;
        const cart = await res.json();
        const val = cart && cart.attributes ? cart.attributes[CartCountdown.ATTRIBUTE_KEY] : null;
        const num = val != null ? parseInt(val, 10) : NaN;
        return Number.isFinite(num) ? num : null;
      } catch (e) {
        return null;
      }
    }

    async setCartAttributeEndTime(endTime) {
      try {
        const updateUrl = (window.routes && window.routes.cart.update) || '/cart/update';
        await fetch(`${updateUrl}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            attributes: { [CartCountdown.ATTRIBUTE_KEY]: endTime.toString() }
          })
        });
      } catch (e) {
        // swallow
      }
    }

    async clearCartAttributeEndTime() {
      try {
        const updateUrl = (window.routes && window.routes.cart.update) || '/cart/update';
        await fetch(`${updateUrl}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            attributes: { [CartCountdown.ATTRIBUTE_KEY]: '' }
          })
        });
      } catch (e) {
        // swallow
      }
    }
  }

  // Only define the custom element if it hasn't been defined yet
  if (!customElements.get('cart-countdown')) {
    customElements.define('cart-countdown', window.CartCountdown);
  }
}
